// src/pages/inventory/InwardForm.jsx
import { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../../components/ui/Input";
import NumberInput from "../../components/ui/NumberInput";
import DateInput from "../../components/ui/DateInput";
import Select from "../../components/ui/Select";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import {
  fetchSuppliers,
  fetchWarehouses,
  fetchProducts,
  createInward,
} from "../../api/inventory";
import Subnav from "../../components/inventory/SubNav";
import { useAlert } from "../../context/AlertContext";

// ---------- Helpers ----------
const todayISO = () => new Date().toISOString().slice(0, 10);
const genInvoiceNo = () => {
  const d = new Date();
  const ymd = d.toISOString().slice(0, 10).replaceAll("-", "");
  const t = `${String(d.getHours()).padStart(2, "0")}${String(
    d.getMinutes()
  ).padStart(2, "0")}${String(d.getSeconds()).padStart(2, "0")}`;
  const rnd = Math.floor(1000 + Math.random() * 9000);
  return `INV-${ymd}-${t}-${rnd}`;
};

// ---------- Schema ----------
const LineItemSchema = z
  .object({
    productId: z.coerce
      .number()
      .int()
      .positive({ message: "Product is required" }),
    lotNo: z.string().min(1, "Lot number is required"),
    unitMode: z.enum(["case", "bottle"]),
    cases: z.coerce.number().int().nonnegative().default(0),
    bottles: z.coerce.number().int().nonnegative().default(0),
    damagedCases: z.coerce.number().int().nonnegative().default(0),
    damagedBottles: z.coerce.number().int().nonnegative().default(0),
    costPerCase: z.coerce
      .number()
      .nonnegative({ message: "Enter cost per case" }),
    taxRate: z.coerce.number().min(0),
    expiryDate: z.string().min(1, "Expiry is required"),
  })
  .superRefine((val, ctx) => {
    // Require quantity in selected unit
    if (val.unitMode === "case" && !val.cases) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter cases",
        path: ["cases"],
      });
    }
    if (val.unitMode === "bottle" && !val.bottles) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter bottles",
        path: ["bottles"],
      });
    }
    // Expiry must be today or future
    const today = todayISO();
    if (val.expiryDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expiry cannot be in the past",
        path: ["expiryDate"],
      });
    }
  });

const FormSchema = z.object({
  supplierId: z.coerce
    .number()
    .int()
    .positive({ message: "Supplier is required" }),
  warehouseId: z.coerce
    .number()
    .int()
    .positive({ message: "Warehouse is required" }),
  invoiceNumber: z.string().min(1, "Invoice # required"),
  invoiceDate: z.string().min(1, "Invoice date required"),
  freight: z.coerce.number().nonnegative().optional(),
  notes: z.string().optional(),
  backdatedReason: z.string().optional(),
  items: z.array(LineItemSchema).min(1, "Add at least one line"),
});

export default function InwardForm({ onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      supplierId: "",
      warehouseId: "",
      invoiceNumber: genInvoiceNo(),
      invoiceDate: todayISO(),
      freight: undefined,
      notes: "",
      backdatedReason: "",
      items: [
        {
          productId: "",
          lotNo: "",
          unitMode: "bottle",
          cases: 0,
          bottles: 0,
          damagedCases: 0,
          damagedBottles: 0,
          costPerCase: 0,
          taxRate: 0,
          expiryDate: "",
        },
      ],
    },
  });

  const { fields, append,prepend, remove } = useFieldArray({ control, name: "items" });
  const invoiceDate = watch("invoiceDate");

  const { showAlert } = useAlert();
  // Load dropdown data
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [s, w, p] = await Promise.all([
          fetchSuppliers(),
          fetchWarehouses(),
          fetchProducts(),
        ]);
        if (!mounted) return;
        setSuppliers(s);
        setWarehouses(w);
        setProducts(p);
      } catch (e) {
        alert(e?.response?.data?.error || "Failed to load form data");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const productById = (id) => products.find((p) => p.id === Number(id));
  const packSize = (pid) => productById(pid)?.case_pack_qty || 0;

  // Derived totals
  const totals = useMemo(() => {
    let bottles = 0;
    let cases = 0;
    let subtotal = 0; // pre-tax
    let tax = 0;
    const items = watch("items") || [];
    items.forEach((it) => {
      const ps = packSize(it.productId) || 1;
      const recvB =
        it.unitMode === "case" ? Number(it.cases) * ps : Number(it.bottles);
      const dmgB = Number(it.damagedCases) * ps + Number(it.damagedBottles);
      const goodB = Math.max(recvB - dmgB, 0);
      bottles += goodB;
      cases += goodB / ps;
      const recvCases =
        it.unitMode === "case"
          ? Number(it.cases)
          : Math.floor(Number(it.bottles) / ps);
      const lineSubtotal = recvCases * Number(it.costPerCase || 0);
      const lineTax = lineSubtotal * (Number(it.taxRate || 0) / 100);
      subtotal += lineSubtotal;
      tax += lineTax;
    });
    return {
      bottles: Math.floor(bottles),
      cases: Number.isFinite(cases) ? cases : 0,
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      total: Number((subtotal + tax).toFixed(2)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("items")]);

  const onSubmit = async (data) => {
    try {
      // backdate rule
      const today = todayISO();
      if (data.invoiceDate < today && !data.backdatedReason) {
        setError("backdatedReason", {
          message: "Backdated invoice requires a reason",
        });
        return;
      }

      // Normalize items to server shape
      const normItems = data.items.map((it) => {
        const ps = packSize(it.productId) || 1;
        // compute received_bottles & damaged_bottles
        const received_bottles =
          it.unitMode === "case" ? Number(it.cases) * ps : Number(it.bottles);
        const damaged_bottles =
          Number(it.damagedCases) * ps + Number(it.damagedBottles);

        if (received_bottles <= 0)
          throw new Error("Each line must have quantity > 0");
        if (damaged_bottles > received_bottles)
          throw new Error("Damaged cannot exceed received");

        return {
          productId: Number(it.productId),
          lotNo: it.lotNo.trim(),
          receivedBottles: received_bottles,
          cases: Number(it.cases),
          damagedBottles: damaged_bottles,
          costPerCase: Number(it.costPerCase),
          taxRate: Number(it.taxRate),
          expiryDate: it.expiryDate,
        };
      });

      const payload = {
        supplierId: Number(data.supplierId),
        warehouseId: Number(data.warehouseId),
        invoiceNumber: data.invoiceNumber.trim(),
        invoiceDate: data.invoiceDate,
        freight: data.freight ? Number(data.freight) : 0,
        notes: data.notes?.trim() || null,
        backdatedReason: data.backdatedReason?.trim() || null,
        items: normItems,
      };
      setSubmitting(true);
      const res = await createInward(payload);
      onSuccess?.(res.id);
      // Reset invoice number for next entry
      setValue("invoiceNumber", genInvoiceNo());
      showAlert({ message: "Inward posted successfully", type: "success" });
    } catch (e) {
      alert(e?.response?.data?.error || e.message || "Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="max-w-5xl mx-auto p-6">Loading…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Subnav />
      <div className="max-w-5xl mx-auto p-6 bg-white rounded-2xl shadow">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Add Inward</h1>
          <div className="text-sm text-gray-600">
            Subtotal: <b>₹{totals.subtotal}</b> · Tax: <b>₹{totals.tax}</b> ·
            Total: <b>₹{totals.total}</b> · Bottles: <b>{totals.bottles}</b>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid md:grid-cols-3 gap-4">
            <Select
              label="Supplier"
              {...register("supplierId")}
              error={errors.supplierId?.message}
            >
              <option value="">Select supplier</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>

            <Select
              label="Warehouse"
              {...register("warehouseId")}
              error={errors.warehouseId?.message}
            >
              <option value="">Select warehouse</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>

            <Input
              label="Invoice #"
              {...register("invoiceNumber")}
              error={errors.invoiceNumber?.message}
            />

            <DateInput
              label="Invoice Date"
              {...register("invoiceDate")}
              error={errors.invoiceDate?.message}
              // default is set in defaultValues; leaving UI explicit
            />

            <NumberInput
              label="Freight (optional)"
              placeholder="0"
              {...register("freight")}
              error={errors.freight?.message}
            />

            <Input
              label="Notes (optional)"
              placeholder="Any remarks"
              {...register("notes")}
              error={errors.notes?.message}
            />

            {invoiceDate && invoiceDate < todayISO() && (
              <Input
                label="Backdated Reason"
                placeholder="Invoice received late, etc."
                {...register("backdatedReason")}
                error={errors.backdatedReason?.message}
              />
            )}
          </div>

          {/* Line Items */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Line Items</h2>
              <button
                type="button"
                className="px-3 py-2 rounded-lg border"
                onClick={() =>
                  prepend({
                    productId: "",
                    lotNo: "",
                    unitMode: "bottle",
                    cases: 0,
                    bottles: 0,
                    damagedCases: 0,
                    damagedBottles: 0,
                    costPerCase: 0,
                    taxRate: 0,
                    expiryDate: "",
                  })
                }
              >
                Add Item
              </button>
            </div>

            <div className="mt-4 grid gap-4">
              {fields.map((f, idx) => {
                const pid = watch(`items.${idx}.productId`);
                const unit = watch(`items.${idx}.unitMode`);
                const ps = packSize(pid);
                const cs = Number(watch(`items.${idx}.cases`) || 0);
                const bs = Number(watch(`items.${idx}.bottles`) || 0);
                const dcs = Number(watch(`items.${idx}.damagedCases`) || 0);
                const dbs = Number(watch(`items.${idx}.damagedBottles`) || 0);

                const recvB = unit === "case" ? cs * ps : bs;
                const dmgB = dcs * ps + dbs;
                const goodB = Math.max(recvB - dmgB, 0);

                return (
                  <div key={f.id} className="p-4 rounded-xl border">
                    <div className="grid md:grid-cols-6 gap-3">
                      <Select
                        label="Product"
                        {...register(`items.${idx}.productId`)}
                        error={errors.items?.[idx]?.productId?.message}
                      >
                        <option value="">Select</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} | {p.brand} | {p.size_label}
                          </option>
                        ))}
                      </Select>

                      <Input
                        label="Lot No."
                        {...register(`items.${idx}.lotNo`)}
                        error={errors.items?.[idx]?.lotNo?.message}
                      />

                      <Select
                        label="Unit"
                        {...register(`items.${idx}.unitMode`)}
                        error={errors.items?.[idx]?.unitMode?.message}
                      >
                        <option value="case" disabled>
                          Cases
                        </option>
                        <option value="bottle">Bottles</option>
                      </Select>

                      {unit === "case" ? (
                        <NumberInput
                          label="Cases"
                          {...register(`items.${idx}.cases`)}
                          error={errors.items?.[idx]?.cases?.message}
                        />
                      ) : (
                        <NumberInput
                          label="Bottles"
                          {...register(`items.${idx}.bottles`)}
                          error={errors.items?.[idx]?.bottles?.message}
                        />
                      )}

                      <NumberInput
                        label="Damaged (Cases)"
                        {...register(`items.${idx}.damagedCases`)}
                        error={errors.items?.[idx]?.damagedCases?.message}
                      />

                      <NumberInput
                        label="Damaged (Bottles)"
                        {...register(`items.${idx}.damagedBottles`)}
                        error={errors.items?.[idx]?.damagedBottles?.message}
                      />

                      <NumberInput
                        label="Purchase Cost / Case"
                        {...register(`items.${idx}.costPerCase`)}
                        error={errors.items?.[idx]?.costPerCase?.message}
                      />

                      <NumberInput
                        label="Tax %"
                        {...register(`items.${idx}.taxRate`)}
                        error={errors.items?.[idx]?.taxRate?.message}
                      />

                      <DateInput
                        label="Expiry"
                        {...register(`items.${idx}.expiryDate`)}
                        error={errors.items?.[idx]?.expiryDate?.message}
                      />
                    </div>

                    <div className="mt-2 text-sm text-gray-600">
                      Pack Size: <b>{ps || "-"}</b> • Received Bottles:{" "}
                      <b>{recvB}</b> • Damaged Bottles: <b>{dmgB}</b> • Good
                      Bottles: <b>{goodB}</b>
                    </div>

                    <div className="mt-2">
                      <button
                        type="button"
                        className="text-red-600 text-sm"
                        onClick={() => remove(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              className="px-5 py-2 rounded-lg bg-black text-white disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? "Posting…" : "Post Inward"}
            </button>
          </div>
        </form>

        <p className="mt-6 text-xs text-gray-500">
          Note: Free bottles per case offers apply to outbound sales only — no
          separation on inward.
        </p>
      </div>
      <Footer />
    </div>
  );
}
